import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Swal from '../../../../helpers/swal';
import clinicService from '../../../../../services/clinic.service';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const PLATFORM_OPTIONS = [
    { value: 'WEBSITE', label: 'Website' },
    { value: 'GOOGLE_PAGE', label: 'Google Business' },
    { value: 'FACEBOOK', label: 'Facebook' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'TWITTER', label: 'Twitter' },
    { value: 'YELP', label: 'Yelp' },
    { value: 'OTHER', label: 'Other' },
];

export const getPlatformIconClass = (type) => {
    switch (type) {
        case 'WEBSITE':
            return 'bi-globe clinic-social-platform-icon--website';
        case 'GOOGLE_PAGE':
            return 'bi-google clinic-social-platform-icon--google';
        case 'FACEBOOK':
            return 'bi-facebook clinic-social-platform-icon--facebook';
        case 'INSTAGRAM':
            return 'bi-instagram clinic-social-platform-icon--instagram';
        case 'TWITTER':
            return 'bi-twitter-x clinic-social-platform-icon--twitter';
        case 'YELP':
            return 'bi-star-fill clinic-social-platform-icon--yelp';
        default:
            return 'bi-link-45deg clinic-social-platform-icon--other';
    }
};

export const formatPlatformName = (type) => type.replace(/_/g, ' ').toLowerCase();

function SocialLinkCard({ linkitem, onUpdate }) {
    const [edit, setEdit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: {
            id: linkitem.id,
            type: linkitem.type,
            clinicId: linkitem.clinicId,
            link: linkitem.link,
        },
    });

    const onSubmit = (data) => {
        setIsSubmitting(true);
        clinicService
            .updateSocialLinks(linkitem.clinicId, data)
            .then(() => {
                toast.success('Link updated successfully', { position: toast.POSITION.BOTTOM_RIGHT });
                setEdit(false);
                onUpdate();
            })
            .catch(() => {
                toast.error('Failed to update link', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm me-2',
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                clinicService
                    .deleteSocialLink(linkitem.clinicId, linkitem.id)
                    .then(() => {
                        onUpdate();
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Social link has been deleted.',
                            icon: 'success',
                            timer: 1500,
                            showConfirmButton: false,
                        });
                    })
                    .catch(() => {
                        Swal.fire('Error!', 'Failed to delete the link.', 'error');
                    });
            }
        });
    };

    const cancelEdit = () => {
        setEdit(false);
        reset();
    };

    return (
        <div className="notifications-panel h-100">
            <div className="notifications-panel__header clinic-social-link-card__header">
                <span className="clinic-social-link-card__platform">
                    {linkitem.thumbnailPath ? (
                        <img src={linkitem.thumbnailPath} alt={linkitem.type} />
                    ) : (
                        <i className={`bi ${getPlatformIconClass(linkitem.type)}`}></i>
                    )}
                    {formatPlatformName(linkitem.type)}
                </span>
                {!edit && (
                    <ActionTooltipButton
                        tooltip="Edit link"
                        tooltipId={`edit-social-${linkitem.id}`}
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setEdit(true)}
                    >
                        <i className="bi bi-pencil"></i>
                    </ActionTooltipButton>
                )}
            </div>

            <div className="notifications-panel__body">
                {!edit ? (
                    <>
                        <div className="clinic-social-link-card__url">
                            <a href={linkitem.link} target="_blank" rel="noopener noreferrer">
                                {linkitem.link} <i className="bi bi-box-arrow-up-right"></i>
                            </a>
                        </div>
                        <div className="clinic-social-link-card__meta">
                            <i className="bi bi-calendar3 me-1"></i>
                            Added {linkitem.createdAt}
                        </div>
                        <div className="clinic-social-link-card__footer">
                            <span className="clinic-social-platform-badge">{linkitem.type.replace(/_/g, ' ')}</span>
                            <ActionTooltipButton
                                tooltip="Delete link"
                                tooltipId={`delete-social-${linkitem.id}`}
                                variant="outline-danger"
                                size="sm"
                                onClick={handleDelete}
                            >
                                <i className="bi bi-trash"></i>
                            </ActionTooltipButton>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-3">
                            <label htmlFor={`type-${linkitem.id}`} className="notifications-form-label">
                                Platform
                            </label>
                            <select id={`type-${linkitem.id}`} className="form-select form-select-sm" {...register('type', { required: true })}>
                                {PLATFORM_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor={`link-${linkitem.id}`} className="notifications-form-label">
                                URL
                            </label>
                            <input
                                type="url"
                                id={`link-${linkitem.id}`}
                                className={`form-control form-control-sm ${errors.link ? 'is-invalid' : ''}`}
                                {...register('link', {
                                    required: 'URL is required',
                                    pattern: {
                                        value: /^https?:\/\/.+/,
                                        message: 'Must start with http:// or https://',
                                    },
                                })}
                            />
                            {errors.link && <div className="invalid-feedback">{errors.link.message}</div>}
                            <input type="hidden" {...register('id')} />
                            <input type="hidden" {...register('clinicId')} />
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-outline-secondary btn-sm flex-fill" onClick={cancelEdit} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-success btn-sm flex-fill" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                    <>
                                        <i className="bi bi-floppy me-1"></i>
                                        Update
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default SocialLinkCard;
